import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";

import {
  ContactMailRounded as ContactMailRoundedIcon,
  EmailRounded as EmailRoundedIcon,
  EmojiPeopleRounded as EmojiPeopleRoundedIcon,
  FactCheckRounded as FactCheckRoundedIcon,
  SettingsSuggestRounded as SettingsSuggestRoundedIcon,
} from "@mui/icons-material";
import { Container, Step, StepLabel, Stepper } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { CampaignType } from "@interfaces/campaign.type";
import { IUser } from "@interfaces/user.type";

import { useUserStore } from "@store/userStore";

import { ColorlibConnector, ColorlibStepIconRoot } from "./CampaignStepperStyles";

interface IColorlibStepIconProperties {
  className?: string;
  hasBeenCompleted?: boolean;
  icon?: number;
  isActive?: boolean;
  isCompleted?: boolean;
}

function replaceLastPartOfURL(url: string, location: string) {
  return url.replace(/\/[^/]*$/, `/${location}`);
}

const steps = ["Account", "Setup", "Compose", "Personalize", "Review"];

/**
 * Renders a step icon component with customizable properties.
 *
 * @param hasBeenCompleted - Indicates if the step represented by the icon has been completed.
 * @param isActive - Indicates if the icon is isActive.
 * @param isCompleted - Indicates if the icon is completed.
 * @param className - Additional CSS class name(s) for the component.
 * @param icon - The icon number to display.
 */
function ColorlibStepIcon({
  className = "",
  hasBeenCompleted = false,
  icon = 1,
  isActive = false,
  isCompleted = false,
}: IColorlibStepIconProperties) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const getIconColor = () => {
    if (isActive) {
      return theme.palette.primary.main;
    }
    if (isCompleted) {
      return theme.palette.common.white; // Completed state color
    }
    return theme.palette.grey[50]; // Default color
  };

  const iconColorStyle = { color: getIconColor() };

  const icons = {
    1: <ContactMailRoundedIcon style={iconColorStyle} />,
    2: <SettingsSuggestRoundedIcon style={iconColorStyle} />,
    3: <EmailRoundedIcon style={iconColorStyle} />,
    4: <EmojiPeopleRoundedIcon style={iconColorStyle} />,
    5: <FactCheckRoundedIcon style={iconColorStyle} />,
  };

  return (
    <ColorlibStepIconRoot
      className={className}
      onClick={() => {
        if (hasBeenCompleted) {
          navigate(replaceLastPartOfURL(location.pathname, steps[icon - 1]!.toLowerCase()));
        }
      }}
      ownerState={{ hasBeenCompleted, isActive, isCompleted }}
    >
      {icons[icon as keyof typeof icons]}
    </ColorlibStepIconRoot>
  );
}

ColorlibStepIcon.propTypes = {
  className: PropTypes.string,
  icon: PropTypes.node,
  isActive: PropTypes.bool,
  isCompleted: PropTypes.bool,
};

interface ICustomizedSteppersProperties {
  activeStep: number;
  campaign: CampaignType;
}

function calculateStepsCompleted(
  campaign: CampaignType,
  user: Pick<IUser, "emailSignature" | "jobTitle" | "organizationName">,
) {
  let stepsCompleted = 0;
  if (user.jobTitle && user.emailSignature && user.organizationName) stepsCompleted++;
  if (campaign.name && campaign.technologySummary) stepsCompleted++;
  if (campaign.emails.length > 0) stepsCompleted = 5;
  return stepsCompleted;
}

/**
 * A custom stepper for campaign creation.
 * @param activeStep - The current active step index.
 * @param campaign - The campaign object.
 */
export default function CustomizedSteppers({ activeStep, campaign }: ICustomizedSteppersProperties) {
  const user = useUserStore();
  const completedSteps = calculateStepsCompleted(campaign, user);
  return (
    <Container maxWidth="md">
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        connector={<ColorlibConnector />}
        sx={{
          pb: { md: 6, xs: 4 },
          pt: { md: 6, xs: 4 },
        }}
      >
        {steps.map((label, index) => (
          <Step completed={index < activeStep} key={label}>
            <StepLabel
              StepIconComponent={(properties) => (
                <ColorlibStepIcon
                  {...properties}
                  hasBeenCompleted={index < completedSteps}
                  icon={index + 1}
                  isActive={index === activeStep}
                  isCompleted={index < activeStep}
                />
              )}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Container>
  );
}
